import { Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { DetailPageService } from '@services/detail-page.service';
import { LoaderService } from '@services/support/loader.service';
import { NavigationService } from '@services/support/navigation.service';
import { UserService } from '@services/user.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { MustMatch } from 'src/app/validators/must-match.validator';
import { PasswordRequirements } from 'src/app/validators/password-requirements.validator';
import { UniqueUsername } from 'src/app/validators/unique-username.validator';
import { AlertModalComponent } from 'src/app/components/modals/alert-modal/alert-modal.component';
import { QRCodeComponent } from 'angularx-qrcode';

import { User } from '../../../../models/User';
import { SafeUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '@services/support/snackbar.service';
import { AuthService } from '@services/auth.service';
import { AppService } from '@services/support/app.service';
import { RoomService } from '@services/remote/room.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-user-detail',
    templateUrl: './user-detail.component.html',
    styleUrls: ['./user-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserDetailComponent implements OnInit, OnDestroy {

  @ViewChild('qr', { static: false }) qrContainer: QRCodeComponent;

  @Input() user?
  @Input() detailMode
  @Input() workflowUserCount
  @Input() workflowUserLimit

  passwordValueSub: Subscription = null;
  emailValueSub: Subscription = null;
  
  requirements = {
    minLength: 6,
    uppercase: false,
    lowercase: false,
    number: false,
    specialCharacter: false
  }
  AZContains: boolean = false;
  azContains: boolean = false;
  numberContains: boolean = false;
  specialCharacterContains: boolean = false;

  userDetailForm = new UntypedFormGroup({
    name: new UntypedFormControl('', [Validators.required, Validators.pattern("^[a-zA-Z0-9ıİğĞüÜşŞöÖçÇ][a-zA-Z0-9ıİğĞüÜşŞöÖçÇ\\-._ ]{0,59}$")]),
    username: new UntypedFormControl('', {
      validators: [Validators.required, Validators.pattern("^[a-zA-Z0-9_-]{1,30}$")],
      asyncValidators: [UniqueUsername.createValidator(this.userService)],
      updateOn: 'change'
    }),
    email: new UntypedFormControl('', [Validators.required, Validators.pattern("^\\w+([\\.-]?\\w+)*@\\w+([\\.-]?\\w+)*(\\.\\w{2,})+$")]),
    workflowUser: new UntypedFormControl(),
    userWorkflows: new UntypedFormControl(),
    password: new UntypedFormControl('', {
      asyncValidators: [PasswordRequirements.createValidator(this.userService)],
      updateOn: 'change'
    }),
    confirmPassword: new UntypedFormControl(''),
    editorAccess: new UntypedFormControl(false),
    allow_archiving: new UntypedFormControl(),
    call_archive_perm_needed: new UntypedFormControl(),
    allow_seeing_all_users: new UntypedFormControl(),
    call_full_hd: new UntypedFormControl(),
    allow_join_link: new UntypedFormControl(),
    rooms: new UntypedFormControl(),
    allow_calling: new UntypedFormControl(),
    defaultRoom: new UntypedFormControl(),
    label: new UntypedFormControl()
  }, {
    validators: MustMatch('password', 'confirmPassword')
  })

  userData: any = {
    name: '',
    username: '',
    email: '',
    editorAccess: false,
    allow_archiving: false,
    allow_seeing_all_users: false,
    allow_calling: false,
    call_archive_perm_needed: false,
    call_full_hd: false,
    rooms: []
  };

  changes = {
    uid: '',
    name: '',
    properties: {},
    deleted_rooms: [],
    inserted_rooms: []
  }

  userDetailFormSub: Subscription = null;
  formPristine: boolean = true;
  passwordChangeInvalid: boolean = false;

  sidebarSub: Subscription;
  sidebarClosed: boolean = true;

  qrSub: Subscription = null;
  isQrAvailable: boolean = false;
  qrData: string = "";
  qrCodeDownloadLink: SafeUrl = "";

  userRoles = ['coadmin', 'user']
  selectedRole = 'user'

  currentUser: User

  accountDataSub: Subscription = null;
  accountArchiveAllowed: boolean = false;
  accountJoinLinkAllowed: boolean = false;
  accountCallingAllowed: boolean = false;
  accountCoAdminAllowed: boolean = false;
  accountAutoJoinRoomAllowed: boolean = false;
  integrationAllowed: boolean = false;
  integrationCode: string = null;
  integrationLoading: boolean = false;
  accountFullHdAllowed: boolean = false;
  accountAlwaysFullHd: boolean = false;
  accountRemoteEnabled: boolean = false;
  accountWorkflowEnabled: boolean = false;

  licenseType: string = "concurrent_user_based";

  rooms = [];
  roomsLoading: boolean = true;
  selectedUserRooms: { id: string, name: string }[] = []

  subscriptions: Subscription[] = []

  labels: string[] = [];
  accountLabelsLoading = true;
  accountLabelsSub: Subscription = null;
  loading = false
  generatedUsername = ''
  usernameManuallyEdited = false
  showCreateMoreSettings = false

  constructor(
    private userService: UserService,
    private detailPageService: DetailPageService,
    private loaderService: LoaderService,
    private modalService: BsModalService,
    private navigationService: NavigationService,
    private translateService: TranslateService,
    private snackBarService: SnackbarService,
    private authService: AuthService,
    private appService: AppService,
    private roomService: RoomService
    ) { }

  get form() {
    return this.userDetailForm.controls;
  }

  ngOnInit(): void {
    this.subscriptions.push(this.roomService.rooms.subscribe(rooms => {
      if(rooms) {
        this.rooms = rooms
        .map(room => { return { id: room.id, name: room.room_data.name } })
        .sort((a, b) => a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()));
      this.roomsLoading = false;
      if(this.user && this.user.rooms) {
        const userRooms = []
        Object.keys(this.user.rooms).forEach(resp => {
          userRooms.push({id:resp,...this.user.rooms[resp]})
        })
        this.selectedUserRooms = userRooms.map(rid => this.rooms.find(r => r.id === rid.id))
      }
      }
    })) 

    this.currentUser = this.authService.currentUser;
    this.authService.getAccountData().then(resp => {
      if(resp) {
        this.licenseType = resp.data.accountData.license.type;
        this.accountRemoteEnabled = resp.data.accountData.license.remote_account ? true : false;
        this.accountWorkflowEnabled = resp.data.accountData.license.workflow_account ? true : false;
        this.accountArchiveAllowed = resp.data.accountData.features.archiving ? true : false;
        this.accountJoinLinkAllowed = resp.data.accountData.features.joinwithlink ? true : false;
        this.accountCallingAllowed = resp.data.accountData.features.calling ? true : false;
        this.accountCoAdminAllowed = resp.data.accountData.features.coadmin ? true : false;
        this.accountAutoJoinRoomAllowed = resp.data.accountData.add_ons.autojoinroom ? true : false;
        this.integrationAllowed = resp.data.accountData.add_ons.sso ? true : false;
        this.accountFullHdAllowed = resp.data.accountData.add_ons.fullhd ? true : false;
        this.accountAlwaysFullHd = resp.data.accountData.always_full_hd ? true : false;

        if(this.licenseType === 'expert_user_based') {
          this.userRoles = ['coadmin', 'user', 'expert']
        }
      }
    })

    if (environment.design.showLabels) {
      this.accountLabelsSub = this.authService.getAccountLabels()
      .subscribe(labels => {
        this.labels = labels ? labels : [];
        this.accountLabelsLoading = false;
      });
    }

    this.userService.getAllUsernames()
    if (this.user){
      this.userData = {
        name: this.user.name,
        username: this.user.auth.username,
        email: this.user.email,
        editorAccess: this.user.editorAccess ? true : false,
        allow_join_link: this.user.allow_join_link ? true : false,
        allow_archiving: this.user.allow_archiving ? true : false,
        allow_seeing_all_users: this.user.allow_seeing_all_users ? true : false,
        allow_calling: this.user.allow_calling ? true : false,
        call_archive_perm_needed: this.user.call_archive_perm_needed ? true : false,
        call_full_hd: this.user.call_full_hd ? true : false,
        rooms: Object.keys(this.user.rooms ? this.user.rooms : {}).filter(r => !this.user.rooms[r].personal_room && !this.user.rooms[r].meeting_room)
      }
      if (environment.design.showLabels) {
        this.userData.label = this.user.label ? this.user.label : "";
      }

      this.userDetailForm.patchValue(this.userData);
      if (this.user?.role) {
        this.selectedRole = this.user.coadmin ? 'coadmin' : this.user.role
      }

      if (this.user.role === 'admin' || this.user.role === 'coadmin' || this.user.coadmin) {
        this.userDetailForm.controls['editorAccess'].patchValue(true)
        this.userDetailForm.controls['editorAccess'].disable()
        this.userDetailForm.controls['label'].disable()
      } else if (this.user.role === 'expert') {
        this.userDetailForm.controls['editorAccess'].enable()
        this.userDetailForm.controls['label'].enable()
      } else {
        this.userDetailForm.controls['editorAccess'].enable()
        this.userDetailForm.controls['label'].disable()
      }
    }

    if (this.detailMode === 'create') {
      this.userDetailForm.get('username').valueChanges.subscribe(username => {
        this.generatedUsername = username;
      });

      this.emailValueSub = this.userDetailForm.get('email').valueChanges.subscribe(email => {
        const generatedUsername = this.buildUsernameFromEmail(email);
        if (!this.usernameManuallyEdited || !this.userDetailForm.get('username').value) {
          this.generatedUsername = generatedUsername;
          this.userDetailForm.get('username').patchValue(generatedUsername, { emitEvent: false });
        }
      });

      const initialEmail = this.userDetailForm.get('email').value;
      this.generatedUsername = this.buildUsernameFromEmail(initialEmail);
      if (this.generatedUsername) {
        this.userDetailForm.get('username').patchValue(this.generatedUsername, { emitEvent: false });
      }
    }

    if (this.detailMode === 'edit') {
      this.userDetailFormSub = this.userDetailForm.valueChanges.subscribe(f => {

        this.changes = {
          uid: this.user.id,
          name: f.name !== this.userData.name ? f.name : this.userData.name,
          properties: {},
          deleted_rooms: [],
          inserted_rooms: []
        };
        if (f.name !== this.userData.name) { this.changes.properties['name'] = f.name}
        if (f.username !== this.userData.username) { this.changes.properties['username'] = f.username}
        if (f.email !== this.userData.email) { this.changes.properties['email'] = f.email}
        if ((this.selectedRole === 'expert' || this.selectedRole === 'user') && f.editorAccess !== this.userData.editorAccess) { this.changes.properties['editorAccess'] = f.editorAccess}
        if (f.allow_join_link !== this.userData.allow_join_link) { this.changes.properties['allow_join_link'] = f.allow_join_link}
        if (f.allow_archiving !== this.userData.allow_archiving) { this.changes.properties['allow_archiving'] = f.allow_archiving}
        if (f.allow_calling !== this.userData.allow_calling) { this.changes.properties['allow_calling'] = f.allow_calling}
        if (f.allow_seeing_all_users !== this.userData.allow_seeing_all_users) { this.changes.properties['allow_seeing_all_users'] = f.allow_seeing_all_users}
        if (f.call_archive_perm_needed !== this.userData.call_archive_perm_needed) { this.changes.properties['call_archive_perm_needed'] = f.call_archive_perm_needed}
        if (f.call_full_hd !== this.userData.call_full_hd) { this.changes.properties['call_full_hd'] = f.call_full_hd}
        if ((this.user?.coadmin ? 'coadmin' : this.user?.role) !== this.selectedRole) { this.changes.properties['role'] = this.selectedRole }
        if (environment.design.showLabels && (f.label ? f.label : "") !== this.userData.label) {
          this.changes.properties['label'] = f.label ? f.label : ""
        }

        const rooms = Object.keys(this.user.rooms ? this.user.rooms : {}).filter(r => !this.user.rooms[r].personal_room && !this.user.rooms[r].meeting_room)
        const deleted_rooms = rooms.filter(x => !f.rooms.includes(x));
        const inserted_rooms = f.rooms.filter(x => !rooms.includes(x));
        if (deleted_rooms.length > 0) {
          this.changes.deleted_rooms = deleted_rooms;
        } else {
          this.changes.deleted_rooms = [];
        }
        if (inserted_rooms.length > 0) {
          this.changes.inserted_rooms = inserted_rooms;
        } else {
          this.changes.inserted_rooms = [];
        }

        this.formPristine = (
          f.name === this.userData.name &&
          f.username === this.userData.username &&
          f.email === this.userData.email &&
          this.changes.inserted_rooms.length === 0 &&
          this.changes.deleted_rooms.length === 0 &&
          f.allow_join_link === this.userData.allow_join_link &&
          f.allow_archiving === this.userData.allow_archiving &&
          f.allow_calling === this.userData.allow_calling &&
          f.allow_seeing_all_users === this.userData.allow_seeing_all_users &&
          f.call_archive_perm_needed === this.userData.call_archive_perm_needed &&
          f.call_full_hd === this.userData.call_full_hd &&
          ((this.selectedRole === 'expert' || this.selectedRole === 'user') ? f.editorAccess === this.userData.editorAccess : true) &&
          (environment.design.showLabels ? f.label === this.userData.label : true)
        ) ? true : false;
      })
   
      if(this.user.auth.qr) {
        this.isQrAvailable = true;
        this.qrData = this.user.auth.account_name + "!!" + this.user.auth.username + '!!' + this.user.auth.qr + '!!v2';
      }else{
        this.isQrAvailable = false;
        this.qrData = null;
      }
    }

    this.passwordValueSub = this.userDetailForm.get('password').valueChanges.subscribe(value => {
      this.testPasswordRequirements(value);
    });
    let previousAllowCalling = this.userDetailForm.get('allow_calling').value;
    this.userDetailForm.get('allow_calling').valueChanges.subscribe(allowCalling => {
      const allowSeeingAllUsersControl = this.userDetailForm.get('allow_seeing_all_users');
      if (allowCalling && !previousAllowCalling) {
        allowSeeingAllUsersControl.patchValue(true);
      }
      previousAllowCalling = allowCalling;
    });
    this.sidebarSub = this.navigationService.sidebarState.subscribe(state => {
      this.sidebarClosed = state;
    });
    this.requirements = this.userService.passwordRequirements;
  }

  onLabelChange(label: string) {
    if (!label) {
      this.userData.label = ""
    }
  }

  onChangeURL(url: SafeUrl) {
    this.qrCodeDownloadLink = url;
  }

  testPasswordRequirements(password: string){
    this.AZContains = RegExp('.*[A-Z].*').test(password);
    this.azContains = RegExp('.*[a-z].*').test(password);
    this.numberContains = RegExp('.*[0-9].*').test(password);
    this.specialCharacterContains = RegExp('.*[-*/?#&()@%+\'!$^:,~_.½£₺é;{}ß>|æ].*').test(password);
  }

  onBackButton(){
    this.detailPageService.removeComponent();
  }

  onUserSubmitForm(){
    const enteredUsername = this.userDetailForm.get('username').value?.trim();
    const newUser: any = {
      name: this.userDetailForm.get('name').value,
      role: this.selectedRole,
      allow_archiving: this.userDetailForm.get('allow_archiving').value ? true : false,
      allow_join_link: this.userDetailForm.get('allow_join_link').value ? true : false,
      allow_calling: this.userDetailForm.get('allow_calling').value ? true : false,
      allow_seeing_all_users: this.userDetailForm.get('allow_seeing_all_users').value ? true : false,
      call_archive_perm_needed: this.userDetailForm.get('call_archive_perm_needed').value ? true : false,
      call_full_hd: this.userDetailForm.get('call_full_hd').value ? true : false,
      username: this.detailMode === 'create' ? (enteredUsername || this.buildUsernameFromEmail(this.userDetailForm.get('email').value)) : this.userDetailForm.get('username').value,
      email: this.userDetailForm.get('email').value,
      rooms: this.userDetailForm.get('rooms').value,
      editorAccess: this.userDetailForm.get('editorAccess').value
    }
    if (this.userDetailForm.get('defaultRoom').value) {
      newUser.default_room = this.userDetailForm.get('defaultRoom').value;
    }
    if (this.userDetailForm.get('label').value) {
      newUser.label = this.userDetailForm.get('label').value;
    }
    this.createUser(newUser);
  }

  createUser(newUser: User) {
    if(this.workflowUserCount == this.workflowUserLimit && this.workflowUserLimit !== 0) {
      const initialState= {
        textMessage : this.translateService.instant('MAIN.CONTENT.USER-DETAIL.LIMIT-WARNING'),
        confirmButtonText : this.translateService.instant('MAIN.CONTENT.WORKFLOW.TEXT-MESSAGE.OK'),
        confirmButtonStyle : 'primary'
      };
      let alertmodalRef = this.modalService.show(AlertModalComponent, {
        initialState,
        backdrop: 'static',
        class: 'modal-dialog-centered',
        animated: false
      });
      alertmodalRef.content.onClose.subscribe(isConfirm => {
        if(isConfirm) {
          this.saveUser(newUser,false)
        }
      })
    }else{
      this.saveUser(newUser,true)
    }
  }

  saveUser(newUser,limitError) {
    this.userDetailForm.disable();
      this.loaderService.show();
      return this.userService.createUser(newUser)
      .then(result => {
        if (result.invitePending) {
          this.snackBarService.success(this.translateService.instant('MAIN.CONTENT.USER-DETAIL.INVITE-SENT'))
        } else if(!result.workflowUser && limitError) {
          this.snackBarService.success(this.translateService.instant('MAIN.CONTENT.USER-DETAIL.CREATED-SUCCESSFULLY'))
        }
        this.detailPageService.removeComponent();
      })
      .catch(error => {
        this.snackBarService.error(this.translateService.instant('ERRORS.ERROR'))
        this.userDetailForm.enable();
      })
      .finally(() => {
        this.loaderService.hide();
      })
  }

  async onSaveChanges() {
    // Check if password is being changed
    const passwordChanged = !this.userDetailForm.controls['password'].errors && 
                            !this.userDetailForm.controls['confirmPassword'].errors &&
                            this.userDetailForm.get('password').value &&
                            this.userDetailForm.get('password').dirty;
    
    if (passwordChanged) {
      // Show confirmation modal before changing password
      const message = this.translateService.instant('MAIN.USERS.USER_DETAILS.CHANGE_PASSWORD_MODAL.MESSAGE');
      const caution = this.translateService.instant('MAIN.USERS.USER_DETAILS.CHANGE_PASSWORD_MODAL.CAUTION');
      
      const initialState = {
        headerTitle: this.translateService.instant('MAIN.USERS.USER_DETAILS.CHANGE_PASSWORD_MODAL.TITLE'),
        textMessage: `<div>${message}</div><div class="text-danger font-weight-bold mt-2">${caution}</div>`,
        confirmButtonText: this.translateService.instant('MAIN.USERS.USER_DETAILS.CHANGE_PASSWORD_MODAL.CHANGE'),
        cancelButtonText: this.translateService.instant('MAIN.USERS.USER_DETAILS.CHANGE_PASSWORD_MODAL.CANCEL'),
        confirmButtonStyle: 'danger'
      };
      
      let alertModalRef = this.modalService.show(AlertModalComponent, {
        initialState,
        backdrop: 'static',
        class: 'modal-dialog-centered',
        animated: false
      });
      
      alertModalRef.content.onClose.subscribe(async (confirmed) => {
        if (confirmed) {
          this.loaderService.show();
          try {
            await this.onPasswordChange();
            // Remove QR from UI since backend deletes it
            this.isQrAvailable = false;
            if (this.user.auth) {
              this.user.auth.qr = null;
            }
            // Continue with user update if there are other changes
            await this.updateUserDetails();
          } catch (error) {
            console.log(error);
            this.loaderService.hide();
          }
        } else {
          alertModalRef.hide();
        }
      });
    } else {
      // No password change, just update user details
      this.loaderService.show();
      await this.updateUserDetails();
    }
  }

  private updateUserDetails() {
    return this.userService.updateUser(this.changes)
    .then(() => {
      if(this.user.auth?.username == this.currentUser.auth?.username && this.changes.properties?.['editorAccess'] === false) {
        this.appService.isEditorModeSource.next(false)
      }
      this.user.name = this.changes.properties['name'] ? this.changes.properties['name'] : this.user.name;
      this.user.username = this.changes.properties['username'] ? this.changes.properties['username'] : this.user.username;
      this.user.email = this.changes.properties['email'] ? this.changes.properties['email'] : this.user.email;
      this.snackBarService.success(this.translateService.instant('MAIN.USERS.USER_DETAILS.USER_UPDATED'));

      /*this.detailPageService.removeComponent()
      const [instance, onClose] = this.detailPageService.loadComponent(UserDetailComponent);
      instance.user = this.user;
      instance.detailMode = 'edit';*/
    })
    .catch(error => {
      if(error.error) {
        this.snackBarService.error(this.translateService.instant('LOGIN.INTERNAL-ERROR'))
      }
    })
    .finally(() => {
      this.loaderService.hide();
    })
  }

  onPasswordChange() {
    return this.userService.changeUserPassword(this.user.id, this.user.auth.username, this.userDetailForm.get('password').value)
    .then(() => {
      this.userDetailForm.get('password').patchValue("");
      this.userDetailForm.get('password').markAsPristine();
      this.userDetailForm.get('confirmPassword').patchValue("");
      this.userDetailForm.get('confirmPassword').markAsPristine();
      this.snackBarService.success("Password changed.")

      // const [instance, OnClose] = this.detailPageService.loadComponent(UserDetailComponent);
      // instance.user = this.user;
      // instance.detailMode = 'edit';
    })
    .catch(error => {
      this.snackBarService.error("Cannot change password.")
    })
  }

  isInvitePending() {
    return this.user?.inviteStatus === 'pending';
  }

  canResendInvite() {
    return this.detailMode === 'edit' && this.isInvitePending() && !this.user?.ad_user && !this.user?.oauth_user_id;
  }

  canSendResetPassword() {
    return this.detailMode === 'edit' && !this.isInvitePending() && !this.user?.ad_user && !this.user?.oauth_user_id;
  }

  hasEmail() {
    return !!this.user?.email?.trim();
  }

  onResendInvite() {
    this.loaderService.show();
    this.userService.sendUserInvite(this.user.id)
    .then(() => {
      this.snackBarService.success(this.translateService.instant('MAIN.CONTENT.USER-DETAIL.INVITE-RESENT'));
    })
    .catch(() => {
      this.snackBarService.error(this.translateService.instant('LOGIN.INTERNAL-ERROR'));
    })
    .finally(() => this.loaderService.hide());
  }

  onSendResetPassword() {
    this.loaderService.show();
    this.userService.sendUserPasswordReset(this.user.id)
    .then(() => {
      this.snackBarService.success(this.translateService.instant('MAIN.CONTENT.USER-DETAIL.RESET-SENT'));
    })
    .catch(() => {
      this.snackBarService.error(this.translateService.instant('LOGIN.INTERNAL-ERROR'));
    })
    .finally(() => this.loaderService.hide());
  }

  onCreateUsernameInput() {
    this.usernameManuallyEdited = true
  }

  buildUsernameFromEmail(email: string): string {
    if (!email || email.indexOf('@') === -1) {
      return '';
    }

    const emailPrefix = email.split('@')[0].toLowerCase();
    const normalizedPrefix = emailPrefix
      .normalize('NFKD')
      .replace(/[^\x00-\x7F]/g, '')
      .replace(/[^a-z0-9_-]/g, '_')
      .replace(/_+/g, '_')
      .replace(/^[_-]+|[_-]+$/g, '');

    const baseUsername = (normalizedPrefix || 'user').slice(0, 30);
    const existingUsernames = new Set((this.userService.allUsernames || []).map(username => username.toLowerCase()));

    let candidate = baseUsername;
    let suffix = 1;

    while (candidate && existingUsernames.has(candidate.toLowerCase())) {
      const suffixText = `_${suffix}`;
      candidate = `${baseUsername.slice(0, 30 - suffixText.length)}${suffixText}`;
      suffix += 1;
    }

    return candidate;
  }

  deleteUser(userId: string) {
    const initialState = {
      headerTitle: this.translateService.instant('MAIN.CONTENT.WORKFLOW.HEADER-TITLES.DELETE-USER'),
      textMessage : this.translateService.instant('MAIN.CONTENT.USER-DETAIL.DELETE-USER-OR-NOT'),
      confirmButtonText : this.translateService.instant('MAIN.CONTENT.USER-DETAIL.DELETE')
    };
    let alertmodalRef = this.modalService.show(AlertModalComponent, {
      initialState,
      backdrop: 'static',
      class: 'modal-dialog-centered',
      animated: false
    });
    alertmodalRef.content.onClose.subscribe(res => {
      if (res) {
        this.loaderService.show();
        this.userService.deleteUser(userId)
        .then(() => {
          this.snackBarService.success(this.translateService.instant('MAIN.USERS.USER_DETAILS.DELETE_USER_MODAL.DELETED'));
        })
        .catch(() => {
          this.snackBarService.error(this.translateService.instant('MAIN.USERS.USER_DETAILS.DELETE_USER_MODAL.ERROR'));
        })
        .finally(() => {
          this.loaderService.hide();
          this.detailPageService.removeComponent();
        });
      } else {
        alertmodalRef.hide();
      }
    })
  }

  onGenerateQR() {
    this.loaderService.show();
    this.userService.generateQr(this.user.auth.username)
    .then(() => {
      this.isQrAvailable = true;
      this.detailPageService.removeComponent()
    })
    .catch(error => {
      if(error.error) {
        this.snackBarService.error(this.translateService.instant('LOGIN.INTERNAL-ERROR'))
      }
    })
    .finally(() => {
      this.loaderService.hide();
    })
  }

  onRegenerateQR() {
    this.loaderService.show();
    this.userService.generateQr(this.user.auth.username)
    .then(() => {
      this.detailPageService.removeComponent()
    })
    .catch(error => {
      if(error.error) {
        this.snackBarService.error(this.translateService.instant('LOGIN.INTERNAL-ERROR'))
      }
    })
    .finally(() => {
      this.loaderService.hide();
    })
  }

  onDeleteQR() {
    const initialState = {
      headerTitle: ' ',
      textMessage : this.translateService.instant('MAIN.CONTENT.USER-DETAIL.DELETE-QR-OR-NOT'),
      confirmButtonText : this.translateService.instant('MAIN.CONTENT.USER-DETAIL.DELETE'),
    };
    let alertmodalRef = this.modalService.show(AlertModalComponent, {
      initialState,
      backdrop: 'static',
      class: 'modal-dialog-centered',
      animated: false
    });
    alertmodalRef.content.onClose.subscribe(res => {
      if (res) {
        this.isQrAvailable = false;
        this.userService.removeQR(this.user.auth.username)
        .catch(error => {
          if(error.error) {
            this.snackBarService.error(this.translateService.instant('LOGIN.INTERNAL-ERROR'))
          }
        })
      } else {
        alertmodalRef.hide();
      }
    })
  }

  selectRole(role) {
    console.log(role)
    
    this.selectedRole = role
    if (role === 'coadmin') {
      this.userDetailForm.controls['editorAccess'].patchValue(true)
      this.userDetailForm.controls['editorAccess'].disable()
      this.userDetailForm.controls['label'].disable()
      this.userDetailForm.controls['label'].setValue('')
    } else if (role === 'expert') {
      this.userDetailForm.controls['editorAccess'].enable()
      this.userDetailForm.controls['label'].enable()
    } else {
      this.userDetailForm.controls['editorAccess'].enable()
      this.userDetailForm.controls['label'].disable()
      this.userDetailForm.controls['label'].setValue('')
    }
  }

  userRoomsChanged(rooms: any[]) {
    this.selectedUserRooms = rooms
    if (!this.selectedUserRooms.find(r => r.id === this.user.default_room)) {
      this.user.default_room = null
    }
  }

  addTagPromise(name) {
    this.loading = true
    return this.authService.setAccountLabels([...this.labels, name]).then(() => {
      this.labels.push(name);
      this.loading = false
      return name;
    })
  }

  ngOnDestroy() {
    if (this.emailValueSub) { this.emailValueSub.unsubscribe() }
    if (this.passwordValueSub) { this.passwordValueSub.unsubscribe() }
    if (this.sidebarSub) { this.sidebarSub.unsubscribe() }
    if (this.qrSub) { this.qrSub.unsubscribe() }
    if (this.userDetailFormSub) { this.userDetailFormSub.unsubscribe() }
    if (this.accountLabelsSub) { this.accountLabelsSub.unsubscribe() }
    this.detailPageService.removeComponent()
    this.subscriptions.forEach(sub => sub.unsubscribe())
  }


}
